import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Checkbox,
  FormControl,
  Select,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  FilterList as FilterIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import ContactEditDialog from "../components/ContactEditDialog";
import CustomerDetailsPanel from "../components/CustomerDetailsPanel";
import ContactViewModel from "../viewmodels/ContactViewModel";
import Contact from "../models/Contacts";
import ContactApiService from "../services/ContactApiService";
import TagService from "../services/TagService";
import { errorMessage } from "../shared/utils/validators";
import appConfig from "../shared/js/app-config";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ContactImportDialog from "../components/ContactImportDialog";

const SORT_OPTIONS = [
  {
    key: "newest",
    label: "Created (Newest)",
    sortBy: "created_at",
    sortOrder: "desc",
  },
  {
    key: "oldest",
    label: "Created (Oldest)",
    sortBy: "created_at",
    sortOrder: "asc",
  },
];

const ContactListView = () => {
  const [contactViewModel] = useState(() => new ContactViewModel());
  const [contacts, setContacts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("q") || "");
  const deepLinkHandled = useRef(false);
  const [selectedTag, setSelectedTag] = useState('All');
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState("single"); // "single" or "multiple"
  const [contactToDelete, setContactToDelete] = useState(null);
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0].sortBy);
  const [sortOrder, setSortOrder] = useState(SORT_OPTIONS[0].sortOrder);
  const [sortKey, setSortKey] = useState("newest");
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [allContacts, setAllContacts] = useState([]);
  const [loadingAllContacts, setLoadingAllContacts] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
  });
  const [isRefreshing, setIsRefreshing] = useState(true);

  const normalizePhoneForDuplicateCheck = (phone = "") => {
    const digits = phone.replace(/\D/g, "");
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setSearchQuery(q);
  }, [searchParams]);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!appConfig.getServiceId()) {
          await fetchService();
        }
      } catch (error) {
        console.error("Account bootstrap failed:", error);
      }
      await fetch(1);
      setIsRefreshing(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (isRefreshing) return;
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("page");
      const trimmed = searchQuery.trim();
      if (trimmed) params.set("q", trimmed);
      else params.delete("q");
      return params;
    }, { replace: true });
  }, [searchQuery, sortBy, sortOrder, selectedTag]);

  async function fetch(pageNumber = 1) {
    try {
      setLoading(true);

      const res = await ContactApiService.getContactsDetails({
        search: searchQuery?.trim(),
        sortBy: sortBy,
        sortOrder: sortOrder,
        tag: selectedTag.trim(),
        page: pageNumber,
        limit: 25,
      });
      if (res.success) {
        let list = res.data.contacts || [];
        let page = res.data.page || 1;
        let totalPages = res.data.totalPages || 1;

        const contactId = searchParams.get("id");
        const openEdit = searchParams.get("edit") === "1";
        if (!deepLinkHandled.current && (contactId || openEdit)) {
          let match = contactId
            ? list.find((c) => c.getId() === contactId)
            : list[0];
          if (contactId && !match) {
            const byId = await ContactApiService.getContactsDetails({
              search: contactId,
              sortBy,
              sortOrder,
              tag: "All",
              page: 1,
              limit: 25,
            });
            if (byId.success) {
              list = byId.data.contacts || [];
              page = byId.data.page || 1;
              totalPages = byId.data.totalPages || 1;
              match = list.find((c) => c.getId() === contactId) || list[0];
            }
          }
          if (match) {
            setSelectedContact(match);
            if (openEdit) {
              setEditingContact(match);
              setEditDialogOpen(true);
            }
            deepLinkHandled.current = true;
          }
        }

        setContacts(list);
        contactViewModel.contacts = list;
        contactViewModel.filteredContacts = list;
        contactViewModel.contactService.setContacts(list);

        setPagination({
          page,
          totalPages,
        });

      } else {
        setContacts([]);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }
  // Sync with ViewModel
  useEffect(() => {
    const updateContacts = () => {
      const updatedContacts = contactViewModel.getContacts();
      //     const updatedContacts = contacts;
      setContacts(updatedContacts);
      setSelectedContacts(contactViewModel.getSelectedContacts());

      // Auto-select first contact if no contact is currently selected
      if (updatedContacts.length > 0 && !selectedContact) {
        setSelectedContact(updatedContacts[0]);
      }
    };
    TagService.getAllTags().then((res) => setAvailableTags(res));


    return () => {
      contactViewModel.onStateChange = null;
    };
  }, [contactViewModel]);
  async function fetchService() {
    await appConfig.fetch();
  }
  // useEffect(() => {
  //   fetchService();
  //   fetch(pageFromUrl);

  // }, [pageFromUrl]);

  useEffect(() => {
    // fetchService();
    fetch(1);
  }, [searchQuery?.trim(), sortBy, sortOrder, selectedTag?.trim()]);


  const handleSearchChange = async (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    // await contactViewModel.searchContacts(query);
  };

  const handleContactSelection = async (contactId) => {
    console.log("Clicked ID:", contactId);
    console.log("Current page contacts IDs:", contacts.map(c => c.getId()));

    await contactViewModel.toggleContactSelection(contactId);
    setContacts([...contactViewModel.getContacts()]);
    setSelectedContacts(contactViewModel.getSelectedContacts());
  };

  const handleContactRowClick = (contact) => {
    setSelectedContact(contact);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      contactViewModel.selectAllContacts();
    } else {
      contactViewModel.clearSelection();
    }
    setContacts([...contactViewModel.getContacts()]);
    setSelectedContacts(contactViewModel.getSelectedContacts());
  };


  const handlePageChange = async (event, value) => {
    setSelectedContacts([]);
    contactViewModel.clearSelection();
    const params = new URLSearchParams(searchParams);
    // setSearchParams({ page: value });
    params.set('page', value);
    setSearchParams(params);
    //  fetch(value);
    await fetch(value);
  };

  const handleMoreOptionsClick = (event, contact) => {
    setAnchorEl(event.currentTarget);
    setActiveContact(contact);
  };

  const handleMoreOptionsClose = () => {
    setAnchorEl(null);
    setActiveContact(null);
  };

  const handleEditContact = () => {
    setEditingContact(activeContact);
    setEditDialogOpen(true);
    handleMoreOptionsClose();
  };

  const handleDeleteContact = () => {
    if (activeContact) {
      setContactToDelete(activeContact);
      setDeleteType("single");
      setDeleteDialogOpen(true);
    }
    handleMoreOptionsClose();
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setEditDialogOpen(true);
  };

  const handleSaveContact = async (contactData) => {
    const model = new Contact();

    model.setName(contactData.name);
    model.setPhoneNumber(contactData.phone);
    model.setTags(contactData.tags);
    model.setEmail(contactData.email);
    if (editingContact) {
      try {
        model.setId(editingContact.getId());
        await ContactApiService.updateContact(model);
        setSnackbar({
          open: true,
          message: "Contact updated successfully",
          severity: "success",
        });
      } catch (error) {
        const message = errorMessage(error);
        setSnackbar({
          open: true,
          message: message || "Failed to edit contact",
          severity: "error",
        });
      }
    } else {
      try {
        await ContactApiService.createContact(model);
        setSnackbar({
          open: true,
          message: "Contact added successfully",
          severity: "success",
        });
      } catch (error) {
        const message = errorMessage(error);
        setSnackbar({
          open: true,
          message: message || "Failed to add contact",
          severity: "error",
        });
      }
    }
    fetch();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", "1");
      return params;
    });
  };

  const handleDeleteSelected = () => {
    setDeleteType("multiple");
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteType === "single" && contactToDelete) {
      try {
        const result = await ContactApiService.deleteContact(
          contactToDelete.getId(),
        );
        setSelectedContact(null);
        setSnackbar({
          open: true,
          message: result.message || "Contact deleted successfully",
          severity: "success",
        });
      } catch (error) {
        const message = errorMessage(error);
        setSnackbar({
          open: true,
          message: message || "Failed to delete contact",
          severity: "error",
        });
      }
    } else if (deleteType === "multiple") {
      const result = await contactViewModel.deleteSelectedContacts();
      setSelectedContacts([]);
      setSelectedContact(null);
      if (result.success) {
        setSnackbar({
          open: true,
          message: result.message || "Selected contacts deleted successfully",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: result.error || "Failed to delete contacts",
          severity: "error",
        });
      }
    }
    setDeleteDialogOpen(false);
    setContactToDelete(null);
    // fetch();
    setSearchParams({ page: 1 });
    fetch(1);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };

  const fetchAllContactsForImport = async () => {
    setLoadingAllContacts(true);
    let allContactsData = [];
    let currentPage = 1;
    let hasMore = true;

    try {
      while (hasMore) {
        const res = await ContactApiService.getContactsDetails({
          search: '',
          sortBy: 'created_at',
          sortOrder: 'desc',
          tag: 'All',
          page: currentPage,
          limit: 100,
        });

        if (res.success && res.data.contacts) {
          allContactsData = [...allContactsData, ...res.data.contacts];
          hasMore = currentPage < res.data.totalPages;
          currentPage++;
        } else {
          hasMore = false;
        }
      }
      setAllContacts(allContactsData);
    } catch (error) {
      console.error("Error fetching all contacts:", error);
    } finally {
      setLoadingAllContacts(false);
    }
  };

  //  Handle import dialog open
  const handleOpenImportDialog = async () => {
    setImportDialogOpen(true);
    await fetchAllContactsForImport();
  };

  // Handle bulk import from CSV
  const handleImportContacts = async (contactData) => {
    try {
      const model = new Contact();
      model.setName(contactData.name);
      let phoneNumber = contactData.phone;
      const digits = phoneNumber.replace(/\D/g, "");
      const normalizedIncoming = normalizePhoneForDuplicateCheck(phoneNumber);
      const duplicateExists = allContacts.some((contact) => {
        const existingPhone = contact.getPhoneNumber?.() || "";
        return normalizePhoneForDuplicateCheck(existingPhone) === normalizedIncoming;
      });

      if (duplicateExists) {
        return { success: false, error: "Phone number already exists" };
      }

      if (digits.length === 11 || digits.length === 12) {
        phoneNumber = `+${digits}`;
      }
      model.setPhoneNumber(phoneNumber);
      const response = await ContactApiService.createContact(model);
      if (response?.success === false || response?.error) {
        return {
          success: false,
          error: response?.error || "Failed to create contact",
        };
      }
      const contactId =
        typeof response === "string"
          ? response
          : response?.id ?? null;
      return { success: true, contactId };

    } catch (error) {
      const message = errorMessage(error);
      console.error("Import error:", message);

      // Check for duplicate error
      if (message.includes("duplicate") || message.includes("already exists")) {
        return { success: false, error: "Phone number already exists" };
      }
      return { success: false, error: message };
    }

  };
  const handleImportDialogClose = (success = 0, failed = 0, _meta) => {
    setImportDialogOpen(false);

    if (success > 0 || failed > 0) {
      const hasFailures = failed > 0;
      setSnackbar({
        open: true,
        message: hasFailures
          ? `Imported ${success} contact(s), ${failed} failed`
          : `Imported ${success} contact(s) successfully`,
        severity: hasFailures ? "warning" : "success",
      });
    }

    setPagination((prev) => ({ ...prev, page: 1 }));
    fetch(1);
    setSearchParams({ page: 1 });
    setAllContacts([]);
  };
  const getTagColor = (color) => {
    return color || "#757575";
  };

  /**
   * Map a contact's `source` value to a small platform badge. Today only
   * Shopify is wired; WooCommerce is intentionally pre-mapped because the
   * sibling integration ships next.
   * Returns null for `manual` / unknown / falsy — no badge rendered.
   */
  const getSourceBadge = (source) => {
    const key = String(source || "").toLowerCase();
    if (key === "shopify") return { label: "Shopify", color: "#008060" };
    if (key === "woocommerce") return { label: "WooCommerce", color: "#7f54b3" };
    return null;
  };

  // Calculate selection state based on currently visible contacts
  const visibleSelectedCount = contacts.filter(
    (contact) => contact.isSelected
  ).length;

  const isAllSelected =
    contacts.length > 0 && visibleSelectedCount === contacts.length;
  const isIndeterminate =
    visibleSelectedCount > 0 && visibleSelectedCount < contacts.length;

  console.log("Header selection:", isAllSelected);
  console.log("Selected count:", visibleSelectedCount);
  console.log("Total contacts:", contacts.length);
  console.log("Rendering select all:", contacts.map((c) => c.isSelected));
  console.log("Contacts full object:", contacts);
  console.log("Current page contacts IDs:", contacts.map(c => c.attrs.id));
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top Bar */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #e8eef6",
            borderRadius: 2.5,
            boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", lg: "row" },
              gap: 1.5,
              alignItems: { xs: "stretch", lg: "center" },
              justifyContent: "space-between",
            }}
          >
            <TextField
              placeholder="Search by name or number"
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ color: "#9aa4b2", mr: 1, fontSize: 18 }} />
                ),
              }}
              sx={{
                width: { xs: "100%", lg: 340 },
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: "#f8fafc",
                  fontSize: "0.875rem",
                  fontFamily: "Poppins, sans-serif",
                  "& fieldset": {
                    borderColor: "#dbe3ee",
                  },
                  "&:hover fieldset": {
                    borderColor: "#c4d0df",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1976d2",
                    borderWidth: 1,
                  },
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                gap: 1.25,
                alignItems: "center",
                justifyContent: { xs: "flex-start", lg: "flex-end" },
                width: { xs: "100%", lg: "auto" },
              }}
            >
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={sortKey}
                  onChange={(e) => {
                    const selected = SORT_OPTIONS.find(
                      (opt) => opt.key === e.target.value,
                    );
                    setSortKey(selected.key);
                    setSortBy(selected.sortBy);
                    setSortOrder(selected.sortOrder);
                  }}
                  displayEmpty
                  startAdornment={
                    <FilterIcon sx={{ color: "#94a3b8", mr: 0.75, fontSize: 16 }} />
                  }
                  sx={{
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    fontSize: "0.875rem",
                    "& .MuiSelect-select": {
                      py: 1,
                    },
                    "& fieldset": {
                      borderColor: "#dbe3ee",
                    },
                    "&:hover fieldset": {
                      borderColor: "#c4d0df",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <Select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  displayEmpty
                  startAdornment={
                    <FilterIcon sx={{ color: "#94a3b8", mr: 0.75, fontSize: 16 }} />
                  }
                  sx={{
                    height: 40,
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    fontSize: "0.875rem",
                    "& .MuiSelect-select": {
                      py: 1,
                    },
                    "& fieldset": {
                      borderColor: "#dbe3ee",
                    },
                    "&:hover fieldset": {
                      borderColor: "#c4d0df",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1976d2",
                    },
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: "0.875rem" }}>
                    All
                  </MenuItem>
                  {availableTags.map((tag) => {
                    const color = getTagColor(tag.getColor());
                    return (
                      <MenuItem
                        key={tag.getId()}
                        value={tag.getName()}
                        sx={{
                          fontSize: "0.875rem",
                          color,
                          "&:hover": {
                            backgroundColor: `${color}20`,
                          },
                        }}
                      >
                        {tag.getName()}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
                onClick={handleOpenImportDialog}
                sx={{
                  height: 40,
                  minWidth: 170,
                  borderRadius: 2.25,
                  textTransform: "none",
                  px: 2.25,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  letterSpacing: "0.1px",
                  borderColor: "#1976d2",
                  color: "#1565c0",
                  backgroundColor: "#f8fbff",
                  "&:hover": {
                    borderColor: "#1565c0",
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                  },
                }}
              >
                Import
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={handleAddContact}
                sx={{
                  height: 40,
                  minWidth: 150,
                  borderRadius: 2.25,
                  textTransform: "none",
                  px: 2.5,
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                  letterSpacing: "0.1px",
                  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  boxShadow: "0 6px 14px rgba(25, 118, 210, 0.24)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                    boxShadow: "0 8px 18px rgba(21, 101, 192, 0.32)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Add Contact
              </Button>
            </Box>
          </Box>
          {/* Bulk Actions - Show below the main row when contacts are selected */}
          {selectedContacts.length > 0 && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedContacts.length} contact(s) selected
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleDeleteSelected}
                sx={{ borderRadius: 2 }}
              >
                Delete Selected
              </Button>
            </Box>
          )}
        </Paper>
        {/* ===== EMPTY CONTACTS PLACEHOLDER ===== */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!loading && contacts.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
              color: "#9ca3af",
              py: 6,
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 72, mb: 1.5 }} />

            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 600,
                mb: 0.5,
                fontFamily: "Poppins, sans-serif",
                color: "#374151",
              }}
            >
              No contacts found
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                maxWidth: 300,
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Add a contact or adjust your search to see results
            </Typography>
            {/* Add Contact Button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={handleAddContact}
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                px: 2,
                py: 0.75,
                mt: 3,
                fontSize: "0.875rem",
                fontWeight: 500,
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                boxShadow: "0 2px 4px rgba(25, 118, 210, 0.2)",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                  boxShadow: "0 4px 8px rgba(25, 118, 210, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Contact
            </Button>
          </Box>
        )}

        {/* Table */}
        {!loading && contacts.length > 0 && (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              flex: 1,
              width: "100%",
              borderRadius: 0,
              "& .MuiTable-root": {
                borderCollapse: "separate",
                borderSpacing: 0,
                width: "100%",
              },
            }}
          >
            <Table stickyHeader sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                    }}
                  >
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={handleSelectAll}
                      sx={{ color: "#666" }}

                    />

                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Phone Number
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Tags
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: "#f5f5f5",
                      // borderBottom: "2px solid #e0e0e0",
                      fontWeight: 600,
                      color: "#333",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {contacts.map((contact) => (

                  <TableRow
                    key={contact.getId()}
                    onClick={() => handleContactRowClick(contact)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                      "&:last-child td": {
                        borderBottom: 0,
                      },
                      backgroundColor:
                        selectedContact?.id === contact.getId()
                          // selectedContact?.getId?.() === contactId
                          ? "#e3f2fd"
                          : "transparent",
                    }}
                  >

                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={contact.isSelected || false}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleContactSelection(contact.getId())}
                        sx={{ color: "#666" }}

                      />
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: "#1976d2",
                            fontSize: "0.875rem",
                          }}
                        >
                          {contact.avatar ? (
                            <img src={contact.avatar} alt={contact.getName()} />
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 500,
                                fontFamily: "Poppins, sans-serif",
                              }}
                            >
                              {contact.getName()}
                            </Typography>
                            {(() => {
                              const badge = getSourceBadge(contact.getSource && contact.getSource());
                              return badge ? (
                                <Chip
                                  label={badge.label}
                                  size="small"
                                  sx={{
                                    backgroundColor: `${badge.color}15`,
                                    color: badge.color,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    height: 20,
                                    fontFamily: "Poppins, sans-serif",
                                  }}
                                />
                              ) : null;
                            })()}
                          </Box>
                          {contact.getEmail() && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontFamily: "Poppins, sans-serif" }}
                            >
                              {contact.getEmail()}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body1"
                        sx={{ fontFamily: "Poppins, sans-serif" }}
                      >
                        {contact.getPhoneNumber()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(contact.getTags() || [])
                          .filter((tag) => tag?.getId?.() && tag?.getName?.())
                          .map((tag) => (
                          <Chip
                            key={tag.getId()}
                            label={tag.getName()}
                            size="small"
                            sx={{
                              backgroundColor: `${getTagColor(tag.getColor())}15`,
                              color: getTagColor(tag.getColor()),
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              fontFamily: "Poppins, sans-serif",
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(event) =>
                          handleMoreOptionsClick(event, contact)
                        }
                        sx={{ color: "#666" }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>

                ))}
              </TableBody>
            </Table>

            <Box sx={{
              display: "flex",
              justifyContent: "space-between",  // Space between items
              alignItems: "center",
              p: 2,
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#fff"
            }}>
              {/* Left - Previous Button */}
              <Button
                variant="outlined"
                size="small"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(null, pagination.page - 1)}
                sx={{
                  minWidth: 100,
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                ← Previous
              </Button>

              {/* Center - Current Page Info */}
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 500,
                  fontFamily: 'Poppins, sans-serif',
                  color: '#1976d2'
                }}
              >
                Page {pagination.page} of {pagination.totalPages}
              </Typography>

              {/* Right - Next Button */}
              <Button
                variant="outlined"
                size="small"
                disabled={pagination.page === pagination.totalPages}
                onClick={() => handlePageChange(null, pagination.page + 1)}
                sx={{
                  minWidth: 100,
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                Next →
              </Button>
            </Box>
          </TableContainer>
        )}

        {/* More Options Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMoreOptionsClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
              minWidth: 150,
            },
          }}
        >
          <MenuItem onClick={handleEditContact} sx={{ gap: 1 }}>
            <EditIcon fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem
            onClick={handleDeleteContact}
            sx={{ gap: 1, color: "#d32f2f" }}
          >
            <DeleteIcon fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
        {/*  Import Dialog */}
        <ContactImportDialog
          open={importDialogOpen}
          onClose={handleImportDialogClose}
          onImport={handleImportContacts}
          existingContacts={allContacts}
          loadingExistingContacts={loadingAllContacts}
        />

        {/* Edit Dialog */}
        <ContactEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          contact={editingContact}
          onSave={handleSaveContact}
          availableTags={availableTags}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 400,
            },
          }}
        >
          <DialogTitle id="delete-dialog-title" sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#d32f2f" }}>
              Confirm Delete
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 2 }}>
            <DialogContentText id="delete-dialog-description">
              {deleteType === "single" && contactToDelete ? (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{contactToDelete.name}</strong>?
                  <br />
                  <br />
                  This action cannot be undone.
                </>
              ) : (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{selectedContacts.length} selected contact(s)</strong>
                  ?
                  <br />
                  <br />
                  This action cannot be undone.
                </>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              sx={{
                borderRadius: 1.5,
                textTransform: "none",
                px: 3,
                background: "linear-gradient(135deg, #d32f2f 0%, #c62828 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #c62828 0%, #b71c1c 100%)",
                },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      {/* Customer Details Panel */}
      <CustomerDetailsPanel
        selectedContact={selectedContact}
        onClose={() => setSelectedContact(null)}
        onUpdateContact={handleSaveContact}
        onRefresh={() => fetch()}
      />
    </Box>
  );
};

export default ContactListView;
